import { TimeMode } from '@types';
import { currentTzString, getLocale } from '@utils';
import { GPSCoordinates } from '@utils/index_ts';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

export function useChartTimezoneConfig(
  timeMode: TimeMode,
  gps: GPSCoordinates,
) {
  const currentTimeZoneString = currentTzString({
    timeMode,
    lat: gps.GPSLat,
    long: gps.GPSLong,
  });

  const tzOffset = getLocale({
    timeMode,
    lat: gps.GPSLat,
    long: gps.GPSLong,
    returnOffset: true,
  }) as number;

  const tzString = getLocale({
    timeMode,
    lat: gps.GPSLat,
    long: gps.GPSLong,
    returnOffset: false,
  }) as string;

  return {
    currentTimeZoneString,
    tzOffset,
    tzString,
  };
}

export function getHighchartsTimeConfig(tzOffset: number) {
  return {
    useUTC: true,
    timezoneOffset: tzOffset,
  };
}

export function formatChartDate(
  timestamp: number | string,
  format: string,
  timeMode: TimeMode,
  gps: GPSCoordinates,
): string {
  const tzString = getLocale({
    timeMode,
    lat: gps.GPSLat,
    long: gps.GPSLong,
    returnOffset: false,
  }) as string;

  return dayjs(timestamp).tz(tzString).format(format);
}

export function formatTimestampForDisplay(
  timestamp: string,
  timeMode: TimeMode | undefined,
  gps: GPSCoordinates | undefined,
): string {
  if (!timeMode || !gps) {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }

  return formatChartDate(timestamp, 'MMM D, HH:mm:ss', timeMode, gps);
}

export function getChartXAxisFormatter(
  timeMode: TimeMode,
  gps: GPSCoordinates,
) {
  return function (this: { value: number }) {
    return formatChartDate(this.value, 'MMM DD HH:mm', timeMode, gps);
  };
}
